import { produce } from 'immer';
import { some } from 'lodash/fp';
import { DATA_SOURCE_REFERENCE_DTO_TYPE } from 'app/store/__generated__/types';
import { extractError } from 'app/lib/error';
import { SIGN_OUT } from 'app/store/users/types.js';
import { LOAD_PROJECT } from 'app/store/projects/types.js';
import { toStringFqn } from 'app/lib/utils';
import {
    ADD_SCHEMA_TO_PROJECT,
    ADD_DATASETS_TO_PROJECT,
    OPEN_ADD_SCHEMA_CONFIRMATION,
    CLOSE_ADD_SCHEMA_CONFIRMATION,
    CLEAR_ERROR
} from './types.js';
import { ImportStatus } from './constants';
import { isImportAdded, isImportFinished } from './utils.js';

export const INITIAL_STATE = {
    activeImportsBySpace: {},
    addSchema: {
        isConfirmationOpen: false,
        params: {}
    },
    error: null
};

const getDatasources = (viewsByUid) => {
    const datasourceViews = Object.values(viewsByUid).filter(
        (view) => view._type === DATA_SOURCE_REFERENCE_DTO_TYPE
    );
    return datasourceViews.map((view) => toStringFqn(view.tableFqn));
};

export const addViewsReducer = produce((draft, action) => {
    switch (action.type) {
        case OPEN_ADD_SCHEMA_CONFIRMATION:
            draft.addSchema.isConfirmationOpen = true;
            draft.addSchema.params = action.payload;
            break;

        case CLOSE_ADD_SCHEMA_CONFIRMATION:
        case ADD_SCHEMA_TO_PROJECT:
            draft.addSchema.isConfirmationOpen = false;
            draft.addSchema.params = {};
            break;

        case ADD_DATASETS_TO_PROJECT.STARTED: {
            const { spaceUid, objectsToImport } = action.payload;
            objectsToImport.forEach((fqnObject) => {
                if (!draft.activeImportsBySpace.hasOwnProperty(spaceUid)) {
                    draft.activeImportsBySpace[spaceUid] = {};
                }
                draft.activeImportsBySpace[spaceUid][toStringFqn(fqnObject)] = ImportStatus.PENDING;
            });

            break;
        }
        case ADD_DATASETS_TO_PROJECT.PROGRESS: {
            const { viewRef, database, schema, table } = action.payload;
            const { spaceUid } = viewRef;
            const datasource = toStringFqn({ database, schema, table });
            if (spaceUid in draft.activeImportsBySpace) {
                if (datasource in draft.activeImportsBySpace[spaceUid]) {
                    draft.activeImportsBySpace[spaceUid][datasource] = ImportStatus.ADDED;
                }
            }
            break;
        }
        case ADD_DATASETS_TO_PROJECT.REJECTED: {
            const { spaceUid, objectsToImport } = action.payload;
            objectsToImport.forEach((fqnObject) => {
                if (draft.activeImportsBySpace.hasOwnProperty(spaceUid)) {
                    delete draft.activeImportsBySpace[spaceUid][toStringFqn(fqnObject)];
                }
            });

            draft.error = extractError(action.payload);
            break;
        }
        case ADD_DATASETS_TO_PROJECT.ENDED: {
            const { spaceUid } = action.meta;
            if (!some(isImportAdded, draft.activeImportsBySpace[spaceUid])) {
                delete draft.activeImportsBySpace[spaceUid];
            }
            break;
        }
        case LOAD_PROJECT.SUCCEEDED: {
            const { entityId: spaceId, viewsByUid } = action.payload;
            const datasourceFqn = getDatasources(viewsByUid);

            if (spaceId in draft.activeImportsBySpace) {
                for (const datasource of datasourceFqn) {
                    if (datasource in draft.activeImportsBySpace[spaceId]) {
                        draft.activeImportsBySpace[spaceId][datasource] = ImportStatus.LOADED;
                    }
                }
                if (some(isImportFinished, draft.activeImportsBySpace[spaceId])) {
                    delete draft.activeImportsBySpace[spaceId];
                }
            }
            break;
        }
        case CLEAR_ERROR:
            draft.error = null;
            break;
        case SIGN_OUT:
            return INITIAL_STATE;
        default:
    }
}, INITIAL_STATE);
